/**
 * Invoice PDF Service
 *
 * Generates, stores, retrieves, views, and downloads invoice PDFs.
 * Uses @react-pdf/renderer for client-side PDF generation from
 * billing Invoice data.
 *
 * Storage: In-memory Map (mock). In production, PDFs would be persisted
 * via API calls to an Azure Blob Storage-backed endpoint.
 */

import { pdf } from '@react-pdf/renderer';
import type { ReactElement } from 'react';
import { createElement } from 'react';
import type { DocumentProps } from '@react-pdf/renderer';
import { InvoicePdfTemplate } from '@/components/invoices/InvoicePdfTemplate';
import type {
  InvoicePdfData,
  InvoicePdfTransaction,
  InvoicePdfResult,
  InvoicePdfLineItem,
  CompanyDetails,
  StoredInvoicePdf,
} from '@/types/invoice-pdf';

// ── In-memory store (mock) ────────────────────────────────────────────────
// In production this would be replaced by API calls to the backend
// which persists PDFs in Azure Blob Storage keyed by invoice ID.
const invoicePdfStore = new Map<string, StoredInvoicePdf>();

/** Default company details — would come from organisation settings in production */
const DEFAULT_COMPANY_DETAILS: CompanyDetails = {
  name: 'Access Professional Services',
  addressLine1: 'Loughborough Technology Centre',
  addressLine2: 'Epinal Way',
  city: 'Loughborough',
  postcode: 'LE11 3GE',
  country: 'United Kingdom',
  phone: '+44 (0) 845 345 3300',
  email: 'accounts@theaccessgroup.com',
  vatNumber: 'GB 123 4567 89',
  companyNumber: '01234567',
};

const DEFAULT_PAYMENT_TERMS =
  'Payment is due within 30 days of the invoice date. ' +
  'Please quote the invoice number on all remittances. ' +
  'Late payments may be subject to statutory interest charges.';

/**
 * Build line items from a billing invoice.
 *
 * In production, actual WIP line items would be fetched from the billing API.
 * For now, we synthesise a single line item from the invoice data.
 */
function buildLineItems(transaction: InvoicePdfTransaction): InvoicePdfLineItem[] {
  return [
    {
      description: transaction.description || 'Professional services',
      code: 'SRV-GEN',
      quantity: 1,
      unitPrice: transaction.amount,
      value: transaction.amount,
    },
  ];
}

/**
 * Generate a PDF for the given billing invoice.
 */
export async function generateInvoicePdf(
  transaction: InvoicePdfTransaction,
  companyDetails: CompanyDetails = DEFAULT_COMPANY_DETAILS,
): Promise<InvoicePdfResult> {
  const lineItems = buildLineItems(transaction);

  const pdfData: InvoicePdfData = {
    transaction,
    companyDetails,
    lineItems,
    paymentTerms: DEFAULT_PAYMENT_TERMS,
  };

  const element = createElement(InvoicePdfTemplate, { data: pdfData }) as ReactElement<DocumentProps>;
  const pdfBlob = await pdf(element).toBlob();

  return {
    invoiceKey: transaction.invoiceNumber,
    invoiceNumber: transaction.invoiceNumber,
    pdfBlob,
    generatedAt: new Date(),
  };
}

/**
 * Save a generated PDF to storage (in-memory mock / would POST to backend API).
 *
 * In production this calls:
 *   POST /api/invoices/{id}/pdf  (multipart/form-data with the blob)
 */
export async function saveInvoicePdf(result: InvoicePdfResult): Promise<void> {
  invoicePdfStore.set(result.invoiceKey, {
    invoiceKey: result.invoiceKey,
    invoiceNumber: result.invoiceNumber,
    pdfBlob: result.pdfBlob,
    contentType: 'application/pdf',
    generatedAt: result.generatedAt,
    sizeBytes: result.pdfBlob.size,
  });
}

/**
 * Retrieve a stored PDF by invoice key.
 *
 * In production this calls:
 *   GET /api/invoices/{id}/pdf
 */
export async function getStoredInvoicePdf(
  invoiceKey: string,
): Promise<StoredInvoicePdf | null> {
  return invoicePdfStore.get(invoiceKey) ?? null;
}

/**
 * Check whether a PDF has already been generated for this invoice.
 */
export function hasStoredPdf(invoiceKey: string): boolean {
  return invoicePdfStore.has(invoiceKey);
}

/**
 * Generate, save, and return the PDF result in one step.
 */
export async function generateAndStoreInvoicePdf(
  transaction: InvoicePdfTransaction,
): Promise<InvoicePdfResult> {
  const result = await generateInvoicePdf(transaction);
  await saveInvoicePdf(result);
  return result;
}

/**
 * Open a PDF blob in a new browser tab for viewing / printing.
 */
export function openPdfInNewTab(pdfBlob: Blob): void {
  const url = URL.createObjectURL(pdfBlob);
  window.open(url, '_blank');
  // Revoke after a delay to allow the tab to load
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/**
 * Trigger a download of the PDF blob.
 */
export function downloadPdf(pdfBlob: Blob, filename: string): void {
  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
